import React, { useState } from 'react';
import { Direction, BidCategory, BidRecord, TablePlayer, SeatPosition } from '../../state/ArenaTypes';
import { ArrowUp, ArrowDown, X, Check, Bot, Sparkles, Crown } from 'lucide-react';
import { arenaAudio } from '../../services/ArenaAudioService';
import { getBidPower } from '../../games/BidWhist/BidWhistContractFormatter';
import { motion } from 'framer-motion';
import { AuctionTurnTimerBadge } from './AuctionTurnTimerBadge';
import { GameTableConfig } from '../../engine/GameTableConfig';
import { useArenaStore } from '../../store/useArenaStore';

interface BiddingHudProps {
  currentHighBidRecord: BidRecord | null;
  isMyTurnToBid: boolean;
  onSubmitBid: (bid: number, direction: Direction | undefined, category: BidCategory) => void;
  onPass: () => void;
  bids?: BidRecord[];
  seats?: (TablePlayer | null)[];
  dealerSeat?: SeatPosition;
  mySeat?: SeatPosition | null;
  isActionPending?: boolean;
  turnTimerSeconds?: number | null;
  turnTimerMax?: number;
  config?: GameTableConfig | null;
}

type BidType = 'HIGH' | 'LOW' | 'NT';

interface BidTypeOption {
  id: BidType;
  label: string;
  direction?: Direction;
  category: BidCategory;
}

const BID_TYPES: BidTypeOption[] = [
  {
    id: 'HIGH',
    label: 'Uptown',
    direction: 'UP',
    category: 'TRUMP',
  },
  {
    id: 'LOW',
    label: 'Downtown',
    direction: 'DOWN',
    category: 'TRUMP',
  },
  {
    id: 'NT',
    label: 'No Trump',
    category: 'NT',
  }
];

const BiddingHudComponent: React.FC<BiddingHudProps> = ({
  currentHighBidRecord,
  isMyTurnToBid,
  onSubmitBid,
  onPass,
  bids = [],
  seats = [],
  dealerSeat,
  mySeat,
  isActionPending = false,
  turnTimerSeconds,
  turnTimerMax = 40,
  config
}) => {
  const storeConfig = useArenaStore((s) => s.gameConfig);
  const storeGameState = useArenaStore((s) => s.gameState);
  const effectiveConfig = config ?? storeConfig;
  if (!effectiveConfig) return null;
  const biddingConfig = effectiveConfig.bidding;
  const minBid = Number(biddingConfig.minBid ?? 4);
  const maxBid = Number(biddingConfig.maxBid ?? 7);
  const stickTheDealer = biddingConfig.stickTheDealer;
  const stuckDealerMinBid = Number(biddingConfig.stuckDealerMinBid ?? 4);
  const stuckDealerPassCount = biddingConfig.stuckDealerPassCount ?? 3;
  const eligibleBidLevels = Array.from({ length: Math.max(1, maxBid - minBid + 1) }, (_, i) => minBid + i);

  // Authoritative server stuck dealer & legal bids (handling array or object dictionary)
  const serverLegalBids = React.useMemo(() => {
    if (storeGameState?.phase !== 'BIDDING') return null;
    const raw = storeGameState?.legalBids;
    if (!raw) return null;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'object') return Object.values(raw);
    return null;
  }, [storeGameState?.legalBids, storeGameState?.phase]);

  const allBids: BidRecord[] = React.useMemo(() => {
    if (bids !== undefined && bids !== null && Array.isArray(bids)) {
      return bids as BidRecord[];
    }
    const rawStoreBids = storeGameState?.phase === 'BIDDING' ? storeGameState?.bids : null;
    const storeBids: BidRecord[] = Array.isArray(rawStoreBids)
      ? (rawStoreBids as BidRecord[])
      : (rawStoreBids && typeof rawStoreBids === 'object' ? (Object.values(rawStoreBids) as BidRecord[]) : []);
    return storeBids;
  }, [bids, storeGameState?.bids, storeGameState?.phase]);

  const isPassRecord = (b: BidRecord) => Boolean(b && (b.isPass || Number(b.bid ?? 0) === 0));
  const isStuckDealer = storeGameState?.isStuckDealer !== undefined && storeGameState?.phase === 'BIDDING'
    ? storeGameState.isStuckDealer
    : Boolean(stickTheDealer && (
        (allBids.length === stuckDealerPassCount && allBids.every(isPassRecord)) ||
        (mySeat !== undefined && mySeat !== null && mySeat === dealerSeat && allBids.filter(b => b.seat !== dealerSeat).length === stuckDealerPassCount && allBids.filter(b => b.seat !== dealerSeat).every(isPassRecord))
      ));

  const effectiveHighRecord = React.useMemo(() => {
    let highest: BidRecord | null = null;
    let highestPower = 0;

    for (const b of allBids) {
      if (b && !b.isPass && Number(b.bid ?? 0) >= minBid) {
        const power = getBidPower(Number(b.bid), b.direction, b.category);
        if (power > highestPower) {
          highestPower = power;
          highest = b;
        }
      }
    }
    return highest;
  }, [allBids, minBid]);

  const currentHighPower = effectiveHighRecord
    ? getBidPower(Number(effectiveHighRecord.bid), effectiveHighRecord.direction, effectiveHighRecord.category)
    : 0;

  const isTypeValid = React.useCallback((level: number, typeId: BidType): boolean => {
    const numLevel = Number(level);
    if (numLevel < minBid || numLevel > maxBid) return false;
    const opt = BID_TYPES.find(t => t.id === typeId);
    if (!opt) return false;

    if (isStuckDealer) {
      return numLevel >= stuckDealerMinBid && numLevel <= maxBid;
    }

    const proposedPower = getBidPower(numLevel, opt.direction, opt.category);
    if (proposedPower <= currentHighPower) {
      return false;
    }

    // If server authoritative legal bids are available, ensure consistency
    if (serverLegalBids && serverLegalBids.length > 0) {
      const hasNonPassLegalBids = serverLegalBids.some((b: any) => b && !b.isPass && Number(b.level ?? 0) >= minBid);
      if (hasNonPassLegalBids) {
        return serverLegalBids.some((b: any) =>
          b &&
          !b.isPass &&
          Number(b.level) === numLevel &&
          (b.type === typeId || String(b.type).toUpperCase() === typeId)
        );
      }
    }

    return true;
  }, [minBid, maxBid, isStuckDealer, stuckDealerMinBid, currentHighPower, serverLegalBids]);

  const isLevelEligible = React.useCallback((level: number): boolean => {
    const numLevel = Number(level);
    if (numLevel < minBid || numLevel > maxBid) return false;
    if (isStuckDealer) {
      return numLevel >= stuckDealerMinBid && numLevel <= maxBid;
    }
    return BID_TYPES.some(t => isTypeValid(numLevel, t.id));
  }, [minBid, maxBid, isStuckDealer, stuckDealerMinBid, isTypeValid]);

  const getFirstValidLevel = React.useCallback(() => {
    if (isStuckDealer) return Math.max(minBid, stuckDealerMinBid);
    const validLevel = eligibleBidLevels.find(lvl => isLevelEligible(lvl));
    return validLevel !== undefined ? validLevel : minBid;
  }, [isStuckDealer, minBid, stuckDealerMinBid, eligibleBidLevels, isLevelEligible]);

  const getFirstValidTypeForLevel = React.useCallback((level: number): BidType => {
    const valid = BID_TYPES.find(t => isTypeValid(level, t.id));
    return valid ? valid.id : 'HIGH';
  }, [isTypeValid]);

  const [selectedBid, setSelectedBid] = useState<number>(() => getFirstValidLevel());
  const [selectedType, setSelectedType] = useState<BidType>(() => getFirstValidTypeForLevel(getFirstValidLevel()));

  // Reset selected bid to minimum valid level on fresh auction or new hand number
  const prevHandNumberRef = React.useRef(storeGameState?.handNumber);
  React.useEffect(() => {
    if (storeGameState?.handNumber !== undefined && storeGameState.handNumber !== prevHandNumberRef.current) {
      prevHandNumberRef.current = storeGameState.handNumber;
      const initialLevel = getFirstValidLevel();
      setSelectedBid(initialLevel);
      setSelectedType(getFirstValidTypeForLevel(initialLevel));
    }
  }, [storeGameState?.handNumber, getFirstValidLevel, getFirstValidTypeForLevel]);

  // When all bids transition from non-empty to empty (auction reset), reset selection to the lowest valid level
  const prevBidsCountRef = React.useRef(allBids.length);
  React.useEffect(() => {
    if (prevBidsCountRef.current > 0 && allBids.length === 0) {
      const initialLevel = getFirstValidLevel();
      setSelectedBid(initialLevel);
      setSelectedType(getFirstValidTypeForLevel(initialLevel));
    }
    prevBidsCountRef.current = allBids.length;
  }, [allBids.length, getFirstValidLevel, getFirstValidTypeForLevel]);

  // Auto-correct selected level and type whenever bids change, state updates, or stuck dealer condition activates
  React.useEffect(() => {
    if (isStuckDealer && selectedBid < stuckDealerMinBid) {
      const nextBid = Math.max(minBid, stuckDealerMinBid);
      setSelectedBid(nextBid);
      setSelectedType(getFirstValidTypeForLevel(nextBid));
      return;
    }

    if (!isLevelEligible(selectedBid)) {
      const firstValidLevel = eligibleBidLevels.find(lvl => isLevelEligible(lvl));
      if (firstValidLevel !== undefined) {
        setSelectedBid(firstValidLevel);
        setSelectedType(getFirstValidTypeForLevel(firstValidLevel));
      }
    } else if (!isTypeValid(selectedBid, selectedType)) {
      setSelectedType(getFirstValidTypeForLevel(selectedBid));
    }
  }, [
    selectedBid,
    selectedType,
    currentHighPower,
    isStuckDealer,
    stuckDealerMinBid,
    serverLegalBids,
    allBids,
    isLevelEligible,
    isTypeValid,
    getFirstValidTypeForLevel,
    eligibleBidLevels,
    minBid
  ]);

  const handleSelectLevel = (level: number) => {
    if (!isLevelEligible(level) || isSubmitting) return;
    arenaAudio.playCardSelect();
    setSelectedBid(level);
    if (!isTypeValid(level, selectedType)) {
      setSelectedType(getFirstValidTypeForLevel(level));
    }
  };

  const handleSelectType = (typeId: BidType) => {
    if (!isTypeValid(selectedBid, typeId) || isSubmitting) return;
    arenaAudio.playCardSelect();
    setSelectedType(typeId);
  };

  // Fixed perspective order: You (South) -> West -> Partner (North) -> East
  // Prevents player columns from shuffling positions across hands as dealer rotates.
  const displayPlayerOrder = React.useMemo(() => {
    const base = mySeat !== undefined && mySeat !== null ? mySeat : 0;
    return [
      base as SeatPosition,
      ((base + 1) % 4) as SeatPosition,
      ((base + 2) % 4) as SeatPosition,
      ((base + 3) % 4) as SeatPosition
    ];
  }, [mySeat]);

  const [hasSubmittedLocally, setHasSubmittedLocally] = useState(false);

  React.useEffect(() => {
    if (!isMyTurnToBid) {
      setHasSubmittedLocally(false);
    }
  }, [isMyTurnToBid]);

  React.useEffect(() => {
    if (!hasSubmittedLocally || !isMyTurnToBid) return;
    const timer = setTimeout(() => {
      setHasSubmittedLocally(false);
    }, 4500);
    return () => clearTimeout(timer);
  }, [hasSubmittedLocally, isMyTurnToBid]);

  const isCurrentSelectionValid = isLevelEligible(selectedBid) && isTypeValid(selectedBid, selectedType);
  const isSubmitting = hasSubmittedLocally || isActionPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isMyTurnToBid || !isCurrentSelectionValid || isSubmitting) return;
    const opt = BID_TYPES.find(t => t.id === selectedType);
    if (!opt) return;
    setHasSubmittedLocally(true);
    arenaAudio.playBidChime();
    onSubmitBid(selectedBid, opt.direction, opt.category);
  };

  const handlePass = () => {
    if (!isMyTurnToBid || isStuckDealer || isSubmitting) return;
    setHasSubmittedLocally(true);
    onPass();
  };

  const maxTimer = turnTimerMax && turnTimerMax > 0 ? turnTimerMax : 40;
  const redThreshold = Math.max(5, Math.round(maxTimer * 0.25));
  const warningThreshold = Math.max(10, Math.round(maxTimer * 0.5));

  const isCritical = turnTimerSeconds !== undefined && turnTimerSeconds !== null && turnTimerSeconds <= redThreshold;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 16, x: '-50%' }}
      animate={{
        opacity: hasSubmittedLocally ? 0.75 : 1,
        scale: hasSubmittedLocally ? 0.98 : 1,
        y: 0,
        x: '-50%'
      }}
      exit={{
        opacity: 0,
        scale: 0.95,
        y: 12,
        x: '-50%',
        transition: { duration: 0.2, ease: [0.32, 0, 0.67, 0] }
      }}
      transition={{ type: 'spring', damping: 24, stiffness: 300, mass: 0.8 }}
      className="absolute bottom-[178px] sm:bottom-[198px] md:bottom-[218px] left-1/2 z-40 w-[calc(100%-36px)] max-w-[430px] sm:max-w-[455px] pointer-events-auto select-none rounded-[28px] bg-[#0a1120] transition-[border-color,box-shadow] duration-300 transform-gpu [backface-visibility:hidden] [webkit-backface-visibility:hidden] acs-font-scale-110"
      style={{
        border: isCritical ? '1.5px solid #f43f5e' : '1.5px solid #d48b28',
        boxShadow: isCritical
          ? '0 0 10px 1px rgba(244, 63, 94, 0.5), 0 16px 45px rgba(0, 0, 0, 0.85)'
          : '0 0 10px 1px rgba(229, 142, 25, 0.35), 0 16px 45px rgba(0, 0, 0, 0.85)',
        WebkitBackfaceVisibility: 'hidden',
        willChange: 'transform, opacity',
      }}
    >
      {/* CIRCULAR TIMER BADGE SITTING ON TOP OF THE BORDER (z-30 so border does NOT cut through it) */}
      <AuctionTurnTimerBadge turnTimerSeconds={turnTimerSeconds} turnTimerMax={turnTimerMax} className="absolute -top-7 left-1/2 -translate-x-1/2 z-30" />

      {/* Clean, crisp single card container */}
      <div className="w-full h-full rounded-[27px] px-2.5 sm:px-3 pt-8 sm:pt-9 pb-2 sm:pb-2.5 relative z-10 flex flex-col space-y-1.5 sm:space-y-2 bg-gradient-to-b from-[#0e1728] via-[#09101d] to-[#050912]">
        {/* Watermarks clipped inside inner background layer */}
        <div className="absolute inset-0 rounded-[27px] overflow-hidden pointer-events-none select-none">
          {/* Classic Spade Silhouette (Upper Left - Plump & Proportional matching reference) */}
          <svg
            className="absolute top-0.5 left-1 w-20 h-20 sm:w-24 sm:h-24 rotate-[24deg] text-slate-300/[0.08] select-none pointer-events-none"
            viewBox="0 0 100 100"
            fill="currentColor"
          >
            <path d="M 50 15 C 46 28 14 50 14 66 C 14 78 26 84 42 78 C 39 83 34 89 26 93 L 74 93 C 66 89 61 83 58 78 C 74 84 86 78 86 66 C 86 50 54 28 50 15 Z" />
          </svg>

          {/* Secondary Echo Spade (Below main spade matching reference image) */}
          <svg
            className="absolute top-11 left-10 sm:top-12 sm:left-12 w-9 h-9 sm:w-11 sm:h-11 rotate-[22deg] text-slate-300/[0.045] select-none pointer-events-none"
            viewBox="0 0 100 100"
            fill="currentColor"
          >
            <path d="M 50 15 C 46 28 14 50 14 66 C 14 78 26 84 42 78 C 39 83 34 89 26 93 L 74 93 C 66 89 61 83 58 78 C 74 84 86 78 86 66 C 86 50 54 28 50 15 Z" />
          </svg>

          {/* Two-Tiered Crown Silhouette (Upper Right - Facing Left, Wider, Gradient Fill, Polished) */}
          <svg
            className="absolute top-0.5 right-1 w-22 h-20 sm:w-25 sm:h-22 -rotate-[26deg] select-none pointer-events-none"
            viewBox="0 0 100 100"
          >
            <defs>
              <linearGradient id="crownWatermarkGradBid" x1="20%" y1="0%" x2="80%" y2="100%">
                <stop offset="0%" stopColor="#fde68a" stopOpacity="0.14" />
                <stop offset="45%" stopColor="#f59e0b" stopOpacity="0.08" />
                <stop offset="100%" stopColor="#b45309" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            {/* Bottom tier (base band) */}
            <path
              fill="url(#crownWatermarkGradBid)"
              d="M 14 83 Q 50 86 86 83 L 86 91 Q 50 94 14 91 Z"
            />
            {/* Top tier (main crown with rounded outer prongs, wider stance, and diamond-topped center prong) */}
            <path
              fill="url(#crownWatermarkGradBid)"
              d="M 16 76 L 84 76 L 90 36 C 92 30 82 28 78 34 C 68 44 64 48 57 46 L 57 28 L 66 18 L 50 5 L 34 18 L 43 28 L 43 46 C 36 48 32 44 22 34 C 18 28 8 30 10 36 Z"
            />
          </svg>
        </div>


        {/* SECTION 1: TITLE WITH INTEGRATED SEPARATORS */}
        <div className="flex flex-col items-center justify-center text-center relative z-10 w-full pt-1">
          <div className="flex items-center justify-center gap-3 w-full px-1">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-400/40 to-amber-400/70" />
            <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight shrink-0">
              <span className="sr-only">Your Bid</span>
              <span aria-hidden="true">
                <span className="text-[#f5c544]">Your</span> <span className="text-white">Bid</span>
              </span>
            </h2>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-amber-400/40 to-amber-400/70" />
          </div>
          {isStuckDealer && (
            <div className="text-[11px] text-amber-300 font-semibold italic mt-0.5">
              All passed to dealer — You must bid at least 4
            </div>
          )}
        </div>

        {/* SECTION 2: 4-PLAYER ROW WITH OUTER AND INNER BORDER LINES */}
        <div className="w-full rounded-2xl bg-[#081220]/70 border border-white/15 shadow-[inset_0_1px_2px_rgba(255,255,255,0.06),0_4px_16px_rgba(0,0,0,0.3)] py-2 sm:py-2.5 px-1 sm:px-1.5 relative z-10">
          <div className="grid grid-cols-4 divide-x divide-white/10">
            {displayPlayerOrder.map((seatIdx) => {
              const p = seats?.[seatIdx];
              const isMe = seatIdx === mySeat;
              const seatBid = allBids.filter(b => b && b.seat === seatIdx).pop();
              const isHighBidder = Boolean(effectiveHighRecord && effectiveHighRecord.seat === seatIdx && !effectiveHighRecord.isPass);
              const isDealer = seatIdx === dealerSeat;

              return (
                <div
                  key={seatIdx}
                  className="flex flex-col items-center justify-center text-center px-1 sm:px-1.5 min-w-0"
                >
                  {/* Avatar with Badges */}
                  <div className="relative shrink-0">
                    <div
                      className={`w-10 h-10 sm:w-11 sm:h-11 rounded-full overflow-hidden flex items-center justify-center text-xs font-black bg-slate-900 shadow-md transition-all ${
                        isDealer
                          ? 'border-2 border-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.35)] text-amber-300'
                          : isHighBidder
                          ? 'border-2 border-amber-400 text-amber-300'
                          : 'border-2 border-white/20 text-white'
                      }`}
                    >
                      {p?.isBot ? (
                        <Bot size={17} className={isMe || isDealer ? 'text-amber-400' : 'text-slate-300'} />
                      ) : p?.avatarUrl ? (
                        <img src={p.avatarUrl} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white text-xs sm:text-sm font-bold">
                          {p?.name ? p.name.charAt(0).toUpperCase() : `S${seatIdx + 1}`}
                        </span>
                      )}
                    </div>

                    {/* Crown Icon Badge on 'You' (bottom-left) */}
                    {isMe && (
                      <div className="absolute -bottom-0.5 -left-0.5 w-4 h-4 rounded-full bg-amber-400 text-slate-950 flex items-center justify-center border border-slate-900 shadow-sm z-20">
                        <Crown size={9} className="stroke-[3] fill-slate-950" />
                      </div>
                    )}

                    {/* Dealer Badge 'D' (top-right of avatar) */}
                    {isDealer && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 text-slate-950 text-[7.5px] font-black flex items-center justify-center border border-slate-950 shadow z-20">
                        D
                      </div>
                    )}
                  </div>

                  {/* Player Name with breathing room after avatar */}
                  <span
                    className={`mt-1 text-[11px] sm:text-xs truncate leading-tight w-full max-w-[76px] sm:max-w-[86px] ${
                      isMe ? 'text-[#f5c544] font-bold' : isDealer ? 'text-amber-300 font-bold' : 'text-slate-200 font-semibold'
                    }`}
                    title={p?.name || (isMe ? 'You' : `Player ${seatIdx + 1}`)}
                  >
                    {isMe ? 'You' : (p?.name ? p.name.split(' ')[0] : `S${seatIdx + 1}`)}
                  </span>

                  {/* Status Chip (Current Bid) with breathing room */}
                  <div
                    className={`mt-1 w-full max-w-[52px] sm:max-w-[60px] h-[22px] sm:h-[24px] rounded-md border flex items-center justify-center text-[10px] sm:text-[11px] font-bold leading-none shadow-inner transition-all ${
                      isHighBidder
                        ? 'bg-amber-400/20 border-amber-400/50 text-amber-300'
                        : seatBid && seatBid.isPass
                        ? 'bg-[#0a1322] border-white/10 text-slate-400'
                        : seatBid
                        ? 'bg-[#0a1322] border-white/15 text-slate-100'
                        : 'bg-[#0a1322] border-white/10 text-slate-400'
                    }`}
                  >
                    {seatBid ? (
                      seatBid.isPass ? (
                        <span>Pass</span>
                      ) : (
                        <span className="flex items-center gap-0.5 font-bold">
                          <span>{seatBid.bid}</span>
                          {seatBid.category === 'NT' ? (
                            <span className="text-[8px] font-bold ml-0.5">NT</span>
                          ) : (seatBid.direction === 'DOWN' || String(seatBid.direction).toUpperCase() === 'SPECIAL' || String(seatBid.direction).toUpperCase() === 'LOW') ? (
                            <ArrowDown size={11} strokeWidth={3} className="inline-block -my-0.5 text-rose-500" />
                          ) : (
                            <ArrowUp size={11} strokeWidth={3} className="inline-block -my-0.5 text-emerald-400" />
                          )}
                        </span>
                      )
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SECTION 3: BID CONTROLS WITH SEGMENTED CONTROLS */}
        <form onSubmit={handleSubmit} className="flex flex-col space-y-1.5 sm:space-y-2 w-full relative z-10">
          {/* SEPARATOR LINE ABOVE BID LEVEL SEGMENT CONTROL ONLY */}
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-400/30 to-transparent my-0.5" />

          {/* ROW 1: SEGMENTED CONTROL FOR BID LEVELS */}
          <div
            className="p-1 rounded-xl bg-[#091220]/80 border border-white/15 shadow-inner grid gap-1 relative"
            style={{ gridTemplateColumns: `repeat(${eligibleBidLevels.length}, minmax(0, 1fr))` }}
          >
            {eligibleBidLevels.map((num, idx) => {
              const eligible = isLevelEligible(num);
              const active = selectedBid === num && eligible;
              const prevActive = idx > 0 && selectedBid === eligibleBidLevels[idx - 1] && isLevelEligible(eligibleBidLevels[idx - 1]);
              return (
                <button
                  key={num}
                  type="button"
                  disabled={!eligible || isSubmitting}
                  onClick={() => handleSelectLevel(num)}
                  aria-disabled={!eligible || isSubmitting}
                  className={`relative h-9.5 sm:h-10.5 rounded-lg text-center transition-all flex items-center justify-center select-none font-sans ${
                    active
                      ? 'text-slate-950 font-black'
                      : eligible
                      ? 'text-white/80 hover:text-white hover:bg-white/5 font-black cursor-pointer'
                      : 'text-white/20 bg-white/[0.02] cursor-not-allowed opacity-30 font-bold pointer-events-none'
                  }`}
                >
                  {idx > 0 && !active && !prevActive && (
                    <div className="absolute -left-[2.5px] top-1.5 bottom-1.5 w-px bg-white/15 pointer-events-none" />
                  )}
                  {active && (
                    <motion.div
                      layoutId="activeBidLevel"
                      className="absolute inset-0 bg-gradient-to-b from-[#ffd152] via-[#ffb923] to-[#f59e0b] rounded-lg shadow-[0_0_14px_rgba(245,158,11,0.45)] border border-amber-200/60"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className={`relative z-10 text-lg sm:text-xl font-black leading-none ${!eligible ? 'text-slate-600' : ''}`}>
                    {num}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ROW 2: SEGMENTED CONTROL FOR CONTRACT DIRECTION / CATEGORY (UPTOWN, DOWNTOWN, NO TRUMP) */}
          <div className="p-1 rounded-xl bg-[#091220]/80 border border-white/15 shadow-inner grid grid-cols-3 gap-1 relative">
            {BID_TYPES.map((typeOpt, idx) => {
              const eligible = isTypeValid(selectedBid, typeOpt.id);
              const active = selectedType === typeOpt.id && eligible;
              const prevActive = idx > 0 && selectedType === BID_TYPES[idx - 1].id && isTypeValid(selectedBid, BID_TYPES[idx - 1].id);
              return (
                <button
                  key={typeOpt.id}
                  type="button"
                  disabled={!eligible || isSubmitting}
                  onClick={() => handleSelectType(typeOpt.id)}
                  aria-disabled={!eligible || isSubmitting}
                  className={`relative h-9.5 sm:h-10.5 px-1 rounded-lg text-center transition-all flex items-center justify-center select-none ${
                    active
                      ? 'text-slate-950 font-black'
                      : eligible
                      ? 'text-white/80 hover:text-white hover:bg-white/5 font-bold cursor-pointer'
                      : 'text-white/20 bg-white/[0.02] cursor-not-allowed opacity-30 font-semibold pointer-events-none'
                  }`}
                >
                  {idx > 0 && !active && !prevActive && (
                    <div className="absolute -left-[2.5px] top-1.5 bottom-1.5 w-px bg-white/15 pointer-events-none" />
                  )}
                  {active && (
                    <motion.div
                      layoutId="activeBidType"
                      className="absolute inset-0 bg-gradient-to-b from-[#ffd152] via-[#ffb923] to-[#f59e0b] rounded-lg shadow-[0_0_14px_rgba(245,158,11,0.45)] border border-amber-200/60"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 flex items-center justify-center gap-1.5 min-w-0">
                    {typeOpt.id === 'HIGH' && (
                      <ArrowUp
                        size={15}
                        className={`stroke-[3.5] shrink-0 ${
                          !eligible
                            ? 'text-slate-600'
                            : active
                            ? 'text-emerald-700'
                            : 'text-emerald-400'
                        }`}
                      />
                    )}
                    {typeOpt.id === 'LOW' && (
                      <ArrowDown
                        size={15}
                        className={`stroke-[3.5] shrink-0 ${
                          !eligible
                            ? 'text-slate-600'
                            : active
                            ? 'text-rose-700'
                            : 'text-rose-500'
                        }`}
                      />
                    )}
                    {typeOpt.id === 'NT' && (
                      <Sparkles
                        size={14}
                        className={`shrink-0 ${
                          !eligible
                            ? 'text-slate-600 fill-slate-700'
                            : active
                            ? 'text-slate-950 fill-slate-950'
                            : 'text-amber-400 fill-amber-400/50'
                        }`}
                      />
                    )}
                    <span className={`text-xs sm:text-sm leading-tight uppercase font-bold sm:font-black whitespace-nowrap ${!eligible ? 'text-slate-500' : ''}`}>
                      {typeOpt.label}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* ROW 3: ACTION BUTTONS (PASS & SUBMIT BID) */}
          <div className="grid grid-cols-5 gap-2 pt-0.5">
            {/* Pass Button (Vibrant Ruby Crimson) */}
            <motion.button
              type="button"
              whileHover={(!isStuckDealer && !isSubmitting) ? { scale: 1.015 } : undefined}
              whileTap={(!isStuckDealer && !isSubmitting) ? { scale: 0.97 } : undefined}
              disabled={isStuckDealer || isSubmitting}
              onClick={handlePass}
              className={`col-span-2 h-10.5 sm:h-11 px-2 rounded-xl font-black text-xs sm:text-sm uppercase tracking-wider border flex items-center justify-center gap-1.5 transition-all ${
                isStuckDealer
                  ? 'bg-white/[0.03] border-white/10 text-white/25 cursor-not-allowed shadow-none'
                  : 'bg-gradient-to-r from-[#d81159] via-[#c71f37] to-[#d81159] hover:from-[#e11d48] hover:to-[#be123c] text-white border-rose-400/40 shadow-[0_4px_16px_rgba(216,17,89,0.35)] cursor-pointer'
              }`}
            >
              <X size={16} className="stroke-[3] text-white shrink-0" />
              <span className="whitespace-nowrap">{isStuckDealer ? 'Pass (Forced)' : 'Pass'}</span>
            </motion.button>

            {/* Primary Bid Button (Vibrant Yellow Gold) */}
            <motion.button
              type="submit"
              whileHover={(isCurrentSelectionValid && !isSubmitting) ? { scale: 1.015 } : undefined}
              whileTap={(isCurrentSelectionValid && !isSubmitting) ? { scale: 0.97 } : undefined}
              disabled={!isCurrentSelectionValid || isSubmitting}
              className={`col-span-3 h-10.5 sm:h-11 px-2 sm:px-3 rounded-xl font-black text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all border ${
                isCurrentSelectionValid && !isSubmitting
                  ? 'bg-gradient-to-r from-[#ffd152] via-[#ffb923] to-[#f59e0b] hover:from-[#ffe072] hover:to-[#ffb923] text-slate-950 border-amber-200/70 shadow-[0_4px_16px_rgba(245,158,11,0.45)] cursor-pointer'
                  : 'bg-white/[0.04] text-white/25 border-white/10 cursor-not-allowed shadow-none'
              }`}
            >
              <Check size={17} className="stroke-[3] shrink-0" />
              <span className="whitespace-nowrap truncate">
                {selectedType === 'NT'
                  ? `Bid ${selectedBid} No Trump`
                  : `Bid ${selectedBid} ${selectedType === 'HIGH' ? 'Uptown' : 'Downtown'}`}
              </span>
            </motion.button>
          </div>

          {/* DECORATIVE FOOTER DIVIDER: ─── ♠ ─── (Larger Gold Spade) */}
          <div className="flex items-center justify-center gap-3 w-full pt-1 pb-0.5 select-none relative z-10">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-400/30 to-amber-400/50" />
            <span className="text-amber-400 text-base sm:text-lg select-none leading-none drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]">♠</span>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-amber-400/30 to-amber-400/50" />
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export const BiddingHud = React.memo(BiddingHudComponent);
